import { Component } from '@theme/component';

/**
 * Theme-native wishlist backed by localStorage.
 *
 * - `Wishlist` owns storage and broadcasts `wishlist:update` on `document`.
 * - `<wishlist-button>` toggles a product (product cards, product pages, wishlist page).
 * - `<wishlist-count>` shows the number of saved products (header, mobile drawer).
 * - `<wishlist-page>` renders saved products through the Section Rendering API.
 *
 * Clicks are handled by one delegated listener, and every element re-syncs itself on
 * connect and after section morphs, so cards added by filtering, infinite scroll or
 * quick add pick up the right state without extra wiring.
 */

const STORAGE_KEY = 'shopify_wishlist_108tym';
const STORAGE_VERSION = 1;
const MAX_ITEMS = 50;
const FETCH_CONCURRENCY = 6;

export const WISHLIST_UPDATE_EVENT = 'wishlist:update';

/**
 * @typedef {object} WishlistItem
 * @property {number} productId
 * @property {string} handle
 * @property {number | null} variantId
 * @property {string} [title]
 * @property {number} addedAt
 */

/**
 * @typedef {object} WishlistUpdateDetail
 * @property {'add' | 'remove' | 'sync'} action
 * @property {number[]} productIds
 * @property {WishlistItem[]} items
 */

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toId(value) {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * @param {any} raw
 * @returns {WishlistItem | null}
 */
function normalizeItem(raw) {
  const productId = toId(raw?.productId);
  const handle = typeof raw?.handle === 'string' ? raw.handle.trim() : '';
  if (!productId || !handle) return null;

  return {
    productId,
    handle,
    variantId: toId(raw.variantId),
    title: typeof raw.title === 'string' ? raw.title : undefined,
    addedAt: Number(raw.addedAt) || Date.now(),
  };
}

export class Wishlist {
  /** @returns {WishlistItem[]} */
  static getItems() {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      const items = Array.isArray(data?.items) ? data.items : [];
      const seen = new Set();

      /** @type {WishlistItem[]} */
      const result = [];
      for (const raw of items) {
        const item = normalizeItem(raw);
        if (!item || seen.has(item.productId)) continue;
        seen.add(item.productId);
        result.push(item);
      }
      return result;
    } catch {
      return [];
    }
  }

  /** @param {number | string} productId */
  static has(productId) {
    const id = toId(productId);
    return id !== null && this.getItems().some((item) => item.productId === id);
  }

  /**
   * Adds a product. Products are unique by ID, so adding an existing product is a no-op.
   * @param {Partial<WishlistItem>} input
   * @returns {boolean} Whether the product is in the wishlist afterwards.
   */
  static add(input) {
    const item = normalizeItem({ ...input, addedAt: Date.now() });
    if (!item) return false;

    const items = this.getItems();
    if (items.some((existing) => existing.productId === item.productId)) return true;

    return this.#write([item, ...items].slice(0, MAX_ITEMS), 'add', [item.productId]);
  }

  /**
   * @param {Array<number | string>} productIds
   * @returns {boolean} Whether storage was updated.
   */
  static remove(...productIds) {
    const ids = productIds.map(toId).filter((id) => id !== null);
    const items = this.getItems();
    const remaining = items.filter((item) => !ids.includes(item.productId));
    if (remaining.length === items.length) return false;

    return this.#write(remaining, 'remove', ids);
  }

  /**
   * @param {Partial<WishlistItem>} input
   * @returns {boolean} Whether the product is in the wishlist afterwards.
   */
  static toggle(input) {
    if (this.has(input.productId ?? '')) {
      this.remove(input.productId ?? '');
      return false;
    }
    return this.add(input);
  }

  /**
   * @param {WishlistItem[]} items
   * @param {WishlistUpdateDetail['action']} action
   * @param {number[]} productIds
   */
  static #write(items, action, productIds) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, items }));
    } catch {
      // Storage can be full or blocked (e.g. some private browsing modes).
      return false;
    }
    this.notify(action, productIds, items);
    return true;
  }

  /**
   * @param {WishlistUpdateDetail['action']} action
   * @param {number[]} [productIds]
   * @param {WishlistItem[]} [items]
   */
  static notify(action, productIds = [], items = this.getItems()) {
    document.dispatchEvent(
      new CustomEvent(WISHLIST_UPDATE_EVENT, { detail: { action, productIds, items } })
    );
  }
}

// Keep other tabs and windows in sync.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY || event.key === null) Wishlist.notify('sync');
});

/**
 * Single polite live region for wishlist announcements.
 */
const announcer = {
  /** @type {HTMLElement | null} */
  region: null,

  /** @param {string | undefined} message */
  announce(message) {
    if (!message) return;
    if (!this.region) {
      this.region = document.createElement('div');
      this.region.className = 'visually-hidden';
      this.region.setAttribute('role', 'status');
      this.region.setAttribute('aria-live', 'polite');
      document.body.append(this.region);
    }
    const region = this.region;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  },
};

/**
 * Toggle button for a single product.
 *
 * Attributes:
 * - data-product-id, data-product-handle, data-product-title, data-variant-id
 * - data-wishlist-context: 'card' | 'product' | 'remove'
 *   'product' reads the currently selected variant from the surrounding product form;
 *   'remove' always removes and keeps its label.
 *
 * @extends {Component}
 */
class WishlistButton extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /**
   * Section re-renders (filtering, sorting, variant changes) morph the server's
   * `data-active="false"` onto this live element, and morph's `updatedCallback` targets the
   * parsed node rather than this one. Watching our own attributes restores the saved state.
   */
  #observer = new MutationObserver(() => this.sync());

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.sync, { signal: this.#controller.signal });
    this.sync();
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['active', 'data-active', 'data-product-id', 'aria-label'],
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer.disconnect();
  }

  get button() {
    return this.querySelector('[data-wishlist-toggle]');
  }

  /**
   * Applies the stored state to the button. Writes only on change, so the
   * attribute observer above cannot loop.
   */
  sync = () => {
    const button = this.button;
    if (!(button instanceof HTMLElement) || this.dataset.wishlistContext === 'remove') return;

    const active = Wishlist.has(this.dataset.productId ?? '');
    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;

    if (this.hasAttribute('active') !== active) this.toggleAttribute('active', active);
    if (button.dataset.active !== String(active)) button.dataset.active = String(active);
    if (label) {
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      const text = button.querySelector('[data-wishlist-label]');
      if (text && text.textContent !== label) text.textContent = label;
    }
  };

  toggle() {
    const item = {
      productId: Number(this.dataset.productId),
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      variantId: this.#resolveVariantId(),
    };

    if (this.dataset.wishlistContext === 'remove') {
      if (Wishlist.remove(item.productId)) announcer.announce(this.dataset.messageRemoved);
      return;
    }

    const wasActive = Wishlist.has(item.productId);
    const active = Wishlist.toggle(item);
    if (active !== wasActive) {
      announcer.announce(active ? this.dataset.messageAdded : this.dataset.messageRemoved);
    }
  }

  /** @returns {number | null} */
  #resolveVariantId() {
    if (this.dataset.wishlistContext === 'product') {
      const scope = this.closest('form, dialog, .shopify-section');
      const input = scope?.querySelector('input[name="id"]');
      const selected = input instanceof HTMLInputElement ? toId(input.value) : null;
      if (selected) return selected;
    }
    return toId(this.dataset.variantId);
  }
}

/**
 * Header / drawer counter.
 * Expects `[data-wishlist-count-number]` and optionally `[data-wishlist-count-label]` children.
 */
class WishlistCount extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.render, { signal: this.#controller.signal });
    this.render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  render = () => {
    const count = Wishlist.getItems().length;
    const number = this.querySelector('[data-wishlist-count-number]');
    const label = this.querySelector('[data-wishlist-count-label]');

    if (number) number.textContent = String(count);
    if (label) label.textContent = `${this.dataset.label || ''}: ${count}`;
    this.toggleAttribute('data-empty', count === 0);
  };
}

/**
 * Renders the saved products on the wishlist page.
 *
 * @typedef {object} WishlistPageRefs
 * @property {HTMLElement} grid
 * @property {HTMLElement} empty
 * @property {HTMLElement} [loading]
 *
 * @extends {Component<WishlistPageRefs>}
 */
class WishlistPage extends Component {
  requiredRefs = ['grid', 'empty'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {AbortController | undefined} */
  #renderController;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.#onUpdate, { signal: this.#controller.signal });
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#renderController?.abort();
  }

  /** @param {Event} event */
  #onUpdate = (event) => {
    const detail = /** @type {CustomEvent<WishlistUpdateDetail>} */ (event).detail;

    if (detail?.action === 'remove') {
      let lostFocus = false;
      /** @type {Element | null | undefined} */
      let focusTarget = null;

      for (const id of detail.productIds) {
        const card = this.refs.grid.querySelector(`[data-wishlist-item][data-product-id="${id}"]`);
        if (!card) continue;

        // Keep keyboard focus on the page when the focused card disappears.
        if (card.contains(document.activeElement)) {
          lostFocus = true;
          const neighbour = card.nextElementSibling ?? card.previousElementSibling;
          focusTarget = neighbour?.querySelector('[data-wishlist-toggle]');
        }
        card.remove();
      }

      this.#updateVisibility(false);

      if (lostFocus) {
        const target = focusTarget ?? this.refs.empty.querySelector('a');
        if (target instanceof HTMLElement) target.focus();
      }
      return;
    }

    // Additions (e.g. from another tab) need fresh markup.
    this.render();
  };

  async render() {
    this.#renderController?.abort();
    const controller = new AbortController();
    this.#renderController = controller;

    const items = Wishlist.getItems();
    if (items.length === 0) {
      this.refs.grid.replaceChildren();
      this.#updateVisibility(false);
      return;
    }

    this.#updateVisibility(true);

    const results = await mapWithConcurrency(items, FETCH_CONCURRENCY, (item) =>
      this.#fetchCard(item, controller.signal)
    );
    if (controller.signal.aborted) return;

    /** @type {Element[]} */
    const cards = [];
    /** @type {number[]} */
    const missing = [];

    results.forEach((result, index) => {
      if (result.card) cards.push(result.card);
      else if (result.missing) missing.push(/** @type {WishlistItem} */ (items[index]).productId);
    });

    this.refs.grid.replaceChildren(...cards);
    this.#updateVisibility(false);

    // Drop products that no longer exist on the storefront.
    if (missing.length) Wishlist.remove(...missing);
  }

  /** @param {boolean} loading */
  #updateVisibility(loading) {
    const hasCards = this.refs.grid.querySelector('[data-wishlist-item]') !== null;

    this.toggleAttribute('aria-busy', loading);
    if (this.refs.loading) this.refs.loading.hidden = !loading;
    this.refs.grid.hidden = !hasCards;
    this.refs.empty.hidden = loading || hasCards;
  }

  /**
   * @param {WishlistItem} item
   * @param {AbortSignal} signal
   * @returns {Promise<{ card?: Element, missing?: boolean }>}
   */
  async #fetchCard(item, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${encodeURIComponent(item.handle)}`, window.location.origin);
    url.searchParams.set('section_id', this.dataset.cardSection || 'wishlist-product-card');
    if (item.variantId) url.searchParams.set('variant', String(item.variantId));

    try {
      const response = await fetch(url, { signal });
      if (response.status === 404) return { missing: true };
      if (!response.ok) return {};

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('[data-wishlist-item]');
      if (!(card instanceof HTMLElement)) return {};

      // A handle can be reused by a different product; never show the wrong one.
      if (toId(card.dataset.productId) !== item.productId) return { missing: true };

      return { card: document.importNode(card, true) };
    } catch {
      return {};
    }
  }
}

/**
 * Runs an async mapper over items with a concurrency cap, preserving order.
 * @template T, R
 * @param {T[]} items
 * @param {number} limit
 * @param {(item: T) => Promise<R>} mapper
 * @returns {Promise<R[]>}
 */
async function mapWithConcurrency(items, limit, mapper) {
  /** @type {R[]} */
  const results = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await mapper(/** @type {T} */ (items[index]));
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);

// One delegated listener covers every button, including ones rendered later.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest('[data-wishlist-toggle]');
  const host = trigger?.closest('wishlist-button');
  if (!(host instanceof WishlistButton)) return;

  // Keep the click from reaching card links or enclosing forms.
  event.preventDefault();
  host.toggle();
});
